import {
  Typography,
  LinearProgress,
  Stack,
  Button,
  Select,
  Card,
  Option,
  Grid,
  Input,
  Textarea,
  Divider
} from '@mui/joy';
import { useEffect, useState } from 'react';
import {
  doc,
  onSnapshot,
  getFirestore,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { format, differenceInYears } from 'date-fns';

const EditableDetailItem = ({ label, value, onChange, type = 'text' }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography fontWeight="bold">{label}:</Typography>
    <Input
      type={type}
      value={String(value)}
      onChange={onChange}
      variant="soft"
    />
  </Stack>
);

const Applicant = () => {
  const navigate = useNavigate();
  const [application, setApplication] = useState();
  const [editApplication, setEditApplication] = useState();
  const [isModified, setIsModified] = useState(false);
  const { applicantId } = useParams();

  const handleChange = path => event => {
    setEditApplication(prev => {
      // Create a deep copy of the state to avoid mutating the original state
      const newState = JSON.parse(JSON.stringify(prev));

      // Split the path and reduce to find the last key
      const keys = path.split('.');
      const lastKey = keys.pop();
      const lastObj = keys.reduce((obj, key) => {
        return (obj[key] = obj[key] || {});
      }, newState);

      // Update the value at the last key
      lastObj[lastKey] = event.target.value;
      setIsModified(true);

      return newState;
    });
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(getFirestore(), 'counsellingApplications', applicantId),
      docSnapshot => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setApplication(data);
          setEditApplication(data);
          setIsModified(false);
        } else {
          console.log('No such document!');
        }
      },
      error => {
        console.error('Error fetching application:', error);
      }
    );

    return () => unsubscribe();
  }, [applicantId]);

  if (!editApplication) {
    return <LinearProgress />;
  }

  return (
    <Stack gap={3}>
      <Stack direction="row" justifyContent="space-between">
        <Button
          variant="plain"
          startDecorator={<ArrowBack />}
          onClick={() => navigate('/dashboard/applications')}
        >
          Back to all applications
        </Button>
        <Button
          onClick={async () => {
            const data = {
              ...editApplication,
              submissionDate: Timestamp.fromDate(
                application.submissionDate.toDate()
              )
            };
            await updateDoc(
              doc(getFirestore(), 'counsellingApplications', applicantId),
              data
            );
            setApplication(data);
            setIsModified(false);
          }}
          variant="solid"
          disabled={!isModified}
        >
          Save Changes
        </Button>
      </Stack>
      <Grid container justifyContent="space-evenly">
        <Grid md={4}>
          <Card sx={{ maxWidth: 555 }}>
            <Typography level="title-lg">
              Counselling application by {application.firstName}{' '}
              {application.lastName}
            </Typography>
            <Typography level="body-md">
              Age: {differenceInYears(new Date(), new Date(application.dob))}
            </Typography>
            <Typography level="body-sm">
              Submitted on {format(application.submissionDate.toDate(), 'PPP')}
            </Typography>
            <Select
              value={application.status}
              onChange={(event, newStatus) =>
                updateDoc(
                  doc(getFirestore(), 'counsellingApplications', applicantId),
                  { status: newStatus }
                )
              }
            >
              <Option value="approved">Approved</Option>
              <Option value="pending">Pending</Option>
              <Option value="denied">Denied</Option>
            </Select>
            <Divider />
            <Typography level="title-sm">Internal notes</Typography>
            <Textarea
              minRows={3}
              value={application.internalNotes}
              placeholder="Internal notes..."
              onChange={event =>
                updateDoc(
                  doc(getFirestore(), 'counsellingApplications', applicantId),
                  { internalNotes: event.target.value }
                )
              }
            ></Textarea>
            <Typography level="title-sm" sx={{ mt: 2 }}>
              Number of sessions completed
            </Typography>
            <Input
              type="number"
              value={application.sessionsCompleted || 0}
              slotProps={{
                input: {
                  min: 0
                }
              }}
              onChange={event => {
                const value = Math.max(0, parseInt(event.target.value) || 0);
                updateDoc(
                  doc(getFirestore(), 'counsellingApplications', applicantId),
                  { sessionsCompleted: value }
                );
              }}
            />
            <Divider />

            <Stack gap={1}>
              <EditableDetailItem
                label="First Name"
                value={editApplication.firstName}
                onChange={handleChange('firstName')}
              />
              <EditableDetailItem
                label="Last Name"
                value={editApplication.lastName}
                onChange={handleChange('lastName')}
              />

              <EditableDetailItem
                label="Gender"
                value={editApplication.gender}
                onChange={handleChange('gender')}
              />
              <EditableDetailItem
                label="Date of birth"
                value={editApplication.dob}
                onChange={handleChange('dob')}
                type="date"
              />
              <EditableDetailItem
                label="Pronouns"
                value={editApplication.pronouns}
                onChange={handleChange('pronouns')}
              />
              <EditableDetailItem
                label="Preferred name"
                value={editApplication.preferredName}
                onChange={handleChange('preferredName')}
              />
              <EditableDetailItem
                label="Email"
                value={editApplication.email}
                onChange={handleChange('email')}
              />
              <EditableDetailItem
                label="Phone"
                value={editApplication.phone}
                onChange={handleChange('phone')}
              />
              <Typography level="title-md">Address</Typography>
              <EditableDetailItem
                label="Line1"
                value={editApplication.address.line1}
                onChange={handleChange('address.line1')}
              />
              <EditableDetailItem
                label="City"
                value={editApplication.address.city}
                onChange={handleChange('address.city')}
              />
              <EditableDetailItem
                label="Province"
                value={editApplication.address.province}
                onChange={handleChange('address.province')}
              />
              <EditableDetailItem
                label="Postal code"
                value={editApplication.address.postalCode}
                onChange={handleChange('address.postalCode')}
              />
              <EditableDetailItem
                label="Country"
                value={editApplication.address.country}
                onChange={handleChange('address.country')}
              />
            </Stack>
          </Card>
        </Grid>
        <Grid md={7}>
          <Stack
            gap={3}
            sx={{
              pt: { xs: 3, md: 0 }
            }}
          >
            <Stack gap={1}>
              <Typography level="h3">Counselling details</Typography>
              <EditableDetailItem
                label="Counsellor preferences"
                value={editApplication.counsellorPreference}
                onChange={handleChange('counsellorPreference')}
              />
              <EditableDetailItem
                label="Language"
                value={editApplication.language}
                onChange={handleChange('language')}
              />

              <EditableDetailItem
                label="Previously applied to this program?"
                value={editApplication.previouslyApplied}
                onChange={handleChange('previouslyApplied')}
              />
              <EditableDetailItem
                label="Reason for counselling"
                value={editApplication.reasonForCounselling}
                onChange={handleChange('reasonForCounselling')}
              />
              <EditableDetailItem
                label="Known health issues"
                value={editApplication.knownHealthIssues}
                onChange={handleChange('knownHealthIssues')}
              />
              <EditableDetailItem
                label="Received counselling in the past?"
                value={editApplication.previousCounselling}
                onChange={handleChange('previousCounselling')}
              />
              <EditableDetailItem
                label="Currently receiving counselling?"
                value={editApplication.currentCounselling}
                onChange={handleChange('currentCounselling')}
              />
              <Typography level="title-md">Preferred formats</Typography>
              <EditableDetailItem
                label="In-person"
                value={editApplication.counsellingFormats.inPerson}
                onChange={handleChange('counsellingFormats.inPerson')}
              />
              <EditableDetailItem
                label="Phone"
                value={editApplication.counsellingFormats.phone}
                onChange={handleChange('counsellingFormats.phone')}
              />
              <EditableDetailItem
                label="Video"
                value={editApplication.counsellingFormats.video}
                onChange={handleChange('counsellingFormats.video')}
              />
              <EditableDetailItem
                label="In-person postal code"
                value={editApplication.inPersonPostalCode}
                onChange={handleChange('inPersonPostalCode')}
              />
              <EditableDetailItem
                label="Accommodations"
                value={editApplication.accommodations}
                onChange={handleChange('accommodations')}
              />
            </Stack>
            <Stack gap={1}>
              <Typography level="h3">Finances</Typography>
              <EditableDetailItem
                label="Income range"
                value={editApplication.income}
                onChange={handleChange('income')}
              />
              <EditableDetailItem
                label="Dependants"
                value={editApplication.dependants}
                onChange={handleChange('dependants')}
              />
              <EditableDetailItem
                label="Family financial support"
                value={editApplication.familyFinancialSupport}
                onChange={handleChange('familyFinancialSupport')}
              />
              <EditableDetailItem
                label="Personal savings"
                value={editApplication.personalSavings}
                onChange={handleChange('personalSavings')}
              />
              <EditableDetailItem
                label="Employment status"
                value={editApplication.employmentStatus}
                onChange={handleChange('employmentStatus')}
              />
              <EditableDetailItem
                label="Employment status (specify)"
                value={editApplication.employmentStatusSpecify}
                onChange={handleChange('employmentStatusSpecify')}
              />
              <EditableDetailItem
                label="Government assistance"
                value={editApplication.govAssistance}
                onChange={handleChange('govAssistance')}
              />
              <EditableDetailItem
                label="Living situation"
                value={editApplication.livingSituation}
                onChange={handleChange('livingSituation')}
              />
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default Applicant;
